import { type IActions } from '@interface/IAction';
import { type IPartyCustomization } from '@interface/ICustomization';
import { type IPartnerData } from '@interface/IObserver';
import { type IStates } from '@interface/IReducer';
import { type UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';

import { type VolunteerRole } from '@/interface/IObserver';

// Which Step-1 fields apply to a given role. Observers monitor for a
// developmental partner and (unlike party agents) do not submit a voter's card
// or its VIN, so those inputs — and the card verification slot — are hidden and
// left out of validation for them.
export interface RoleFieldSet {
	requiresVoterCard: boolean;
}

export const fieldsForRole = (role: VolunteerRole): RoleFieldSet => ({
	requiresVoterCard: role === 'agent',
});

// Nigerian phone number regex - supports formats: 08012345678, +2348012345678
const NIGERIAN_PHONE_REGEX = /^(?:\+234|0)([789][01]\d{8})$/;

// Calculate maximum date of birth for minimum age requirement
const getMaxDateForAge = (minAge: number): string => {
	const today = new Date();
	return new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate()).toISOString().split('T')[0];
};

export const MAX_DOB_DATE = getMaxDateForAge(18);

export interface IREADINESSINFO {
	hasSmartPhoneWithInternet: boolean;
	isAvailableAllDay: boolean;
	hasPartyAgentExperience: boolean;
	isNativeSpeaker: boolean;
	hasPowerBank: boolean;
}

export const DEFAULTIREADINESSINFO = {
	hasSmartPhoneWithInternet: false,
	isAvailableAllDay: false,
	hasPartyAgentExperience: false,
	isNativeSpeaker: false,
	hasPowerBank: false,
} as const;

export type READINESSINFOKEYS = keyof typeof DEFAULTIREADINESSINFO;

export const readinessFieldDescriptions: Record<READINESSINFOKEYS, { title: string; subtitle: string }> = {
	hasSmartPhoneWithInternet: {
		title: 'Smartphone with Internet Access?',
		subtitle: 'You’ll need a working smartphone and active internet to receive updates and submit reports.',
	},
	isAvailableAllDay: {
		title: 'Full-Day Availability?',
		subtitle: 'Confirm that you’ll be available throughout the day during the election or event.',
	},
	hasPartyAgentExperience: {
		title: 'Previous Party Agent Experience ?',
		subtitle: "Let us know if you've served as a party agent before.",
	},
	isNativeSpeaker: {
		title: 'Fluent in Local Language ?',
		subtitle: 'Indicate if you speak the local language of the area you’ll be assigned to.',
	},
	hasPowerBank: {
		title: 'Access to a Power Bank?',
		subtitle: 'Let us know if you have one available.',
	},
};

export const personalDetailsSchema = yup.object({
	firstName: yup.string().required('First Name is required'),
	lastName: yup.string().required('Last Name is required'),
	email: yup.string().required('Email is required').email('Please enter a valid email address'),
	phoneNumber: yup
		.string()
		.required('Phone number is required')
		.matches(NIGERIAN_PHONE_REGEX, 'Please enter a valid Nigerian phone number (e.g., 08012345678 or +2348012345678)'),
	votersCardRegistrationNumber: yup.string().required('Voter Card ID is required'),
	votersCardDocument: yup
		.mixed<File>()
		.nullable()
		.test('fileSize', 'File size must be less than 5MB', (value) => {
			if (!value) return true;
			return value instanceof File && value.size <= 5 * 1024 * 1024;
		})
		.test('fileType', 'Only image or PDF files allowed (JPG, PNG, PDF)', (value) => {
			if (!value) return true;
			return value instanceof File && ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(value.type);
		})
		.default(null),
	gender: yup.string().required('Gender is required'),
	dateOfBirth: yup
		.string()
		.required('Date of Birth is required')
		.test('age', 'You must be at least 18 years old', (value) => {
			if (!value) return false;
			const birthDate = new Date(value);
			const today = new Date();
			let age = today.getFullYear() - birthDate.getFullYear();
			const monthDiff = today.getMonth() - birthDate.getMonth();
			if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
				age--;
			}
			return age >= 18;
		}),
	profilePhoto: yup
		.mixed<File>()
		.nullable()
		.test('fileSize', 'File size must be less than 5MB', (value) => {
			if (!value) return true;
			return value instanceof File && value.size <= 5 * 1024 * 1024; // 5MB
		})
		.test('fileType', 'Only image files are allowed (JPG, PNG, GIF, WebP)', (value) => {
			if (!value) return true;
			return value instanceof File && ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(value.type);
		})
		.default({} as File),
	profileUrl: yup.string().default(''),
	// KYC outputs (set programmatically by IdentityVerificationCard)
	faceId: yup.string().nullable().default(null),
	votersCardImage: yup.string().nullable().default(null),
	kycReviewRequired: yup.boolean().default(false),
	kycReviewReasons: yup.array().of(yup.string().defined()).default([]),
	// Social media handles (optional)
	facebookHandle: yup.string().default(''),
	twitterHandle: yup.string().default(''),
	instagramHandle: yup.string().default(''),
	// Messaging app connections (optional)
	isWhatsAppConnected: yup.boolean().default(false),
	isTelegramConnected: yup.boolean().default(false),
	// Payout / bank details — required for paying volunteers
	accountName: yup.string().required('Account name is required'),
	accountNumber: yup
		.string()
		.required('Account number is required')
		.matches(/^\d{10}$/, 'Account number must be 10 digits'),
	bankName: yup.string().required('Bank is required'),
});

export type IPersonalDetailsSchema = yup.InferType<typeof personalDetailsSchema>;

// Role-aware Step-1 validation. Agents keep the full schema (VIN required);
// observers get the same shape with the voter's card VIN relaxed to optional,
// since they don't submit a voter's card. The inferred type stays the agent
// superset so the form's field set is unchanged.
export const makePersonalDetailsSchema = (role: VolunteerRole = 'agent') =>
	fieldsForRole(role).requiresVoterCard
		? personalDetailsSchema
		: personalDetailsSchema.shape({ votersCardRegistrationNumber: yup.string().default('') });

export const locationDetailsSchema = yup.object({
	entityCode: yup.string().required('Polling Unit Code is required'),
	entityName: yup.string().required('Polling Unit Name is required'),
	wardOfResidence: yup.number().nullable().default(null),
	latitude: yup
		.number()
		.required('Location access is required to proceed')
		.min(-90, 'Invalid latitude')
		.max(90, 'Invalid latitude'),
	longitude: yup
		.number()
		.required('Location access is required to proceed')
		.min(-180, 'Invalid longitude')
		.max(180, 'Invalid longitude'),
	// Parsed coordinates of the CHOSEN polling unit (null when INEC has not
	// published them). Stored here so the Summary step can show "Distance from
	// you" off the same form. Never validated and never sent to the API —
	// `buildAndSubmit` picks its fields explicitly. (`.default(null)` rather than
	// `.notRequired()`: an optional key makes yupResolver's output type diverge
	// from the input type and the UseFormReturn no longer type-checks.)
	puLatitude: yup.number().nullable().default(null),
	puLongitude: yup.number().nullable().default(null),
});

export type ILocationDetailsSchema = yup.InferType<typeof locationDetailsSchema>;

export const fieldReadinessSchema = yup.object({
	resultType: yup.string().required('Please select a volunteer role'),
	hasSmartPhoneWithInternet: yup.boolean().default(false),
	isAvailableAllDay: yup.boolean().default(false),
	hasPartyAgentExperience: yup.boolean().default(false),
	isNativeSpeaker: yup.boolean().default(false),
	hasPowerBank: yup.boolean().default(false),
});

export type IFieldReadinessSchema = yup.InferType<typeof fieldReadinessSchema>;

export const makeFieldReadinessSchema = (role: VolunteerRole = 'agent') => fieldReadinessSchema;

export const summarySchema = yup.object({
	tc: yup.boolean().default(false),
});

export type ISummarySchema = yup.InferType<typeof summarySchema>;

export type tabTitle = 'Basic Information' | 'Location Information' | 'Field Readiness' | 'Summary';

export interface ICAChild {
	states?: IStates;
	actions?: IActions;
	guid: string;
	customization?: IPartyCustomization | null;
	// Volunteer lane resolved from the application URL. Defaults to agent.
	role?: VolunteerRole;
	// Developmental partner resolved by GUID for the observer lane (null for agents).
	partnerData?: IPartnerData | null;
	// True when the page was opened from the party's trusted link (`trusted=1`).
	// Drives the step-1 notice and adds `isTrustedAgent: true` to the submit body.
	isTrustedInvite?: boolean;
}

export const initFilterState = {
	stateId: null,
	lgaId: null,
	wardId: null,
	pollingUnitId: null,
};

export type filterKeys = 'stateId' | 'lgaId' | 'wardId' | 'pollingUnitId';

export type newInitFilterState<T> = {
	[K in keyof T]: number | string | null;
};

export function _codeSeparator(code: string) {
	const codeArr = code.trim().split('-');
	if (codeArr.length - 1 === 1)
		return {
			state: codeArr[0],
			lga: codeArr[1],
		};
	if (codeArr.length - 1 === 2)
		return {
			state: codeArr[0],
			lga: codeArr[1],
			ward: codeArr[2],
		};
	if (codeArr.length - 1 === 3)
		return {
			state: codeArr[0],
			lga: codeArr[1],
			ward: codeArr[2],
			pollingunit: codeArr[3],
		};
	return {
		state: code,
	};
}

export type electionType =
	| 'Senate'
	| 'Federal House Of Representatives'
	| 'President'
	| 'State House of Representative'
	| 'Local Government Chairmanship'
	| 'Local Government Councillorship'
	| 'State Governorship';

export interface BasicInfoProps {
	hookForm: UseFormReturn<IPersonalDetailsSchema>;
	onNext: () => void;
	actions: IActions | undefined;
	states: IStates | undefined;
	electionId: number | undefined;
	role?: VolunteerRole;
}

export interface ISelectOptions {
	id: number;
	label: string;
	value: string;
}

export const GENDER_OPTIONS = [
	{ id: 1, label: 'Male', value: 'Male' },
	{ id: 2, label: 'Female', value: 'Female' },
] as const;

// Nigerian commercial + microfinance banks (curated). Used by the Payout Details
// dropdown until a backend lookup endpoint is wired in.
export const NIGERIAN_BANKS: ISelectOptions[] = [
	{ id: 1, label: 'Access Bank', value: 'Access Bank' },
	{ id: 2, label: 'Citibank Nigeria', value: 'Citibank Nigeria' },
	{ id: 3, label: 'Ecobank Nigeria', value: 'Ecobank Nigeria' },
	{ id: 4, label: 'Fidelity Bank', value: 'Fidelity Bank' },
	{ id: 5, label: 'First Bank of Nigeria', value: 'First Bank of Nigeria' },
	{ id: 6, label: 'First City Monument Bank (FCMB)', value: 'First City Monument Bank' },
	{ id: 7, label: 'Globus Bank', value: 'Globus Bank' },
	{ id: 8, label: 'Guaranty Trust Bank (GTBank)', value: 'Guaranty Trust Bank' },
	{ id: 9, label: 'Heritage Bank', value: 'Heritage Bank' },
	{ id: 10, label: 'Keystone Bank', value: 'Keystone Bank' },
	{ id: 11, label: 'Kuda Bank', value: 'Kuda Bank' },
	{ id: 12, label: 'Opay', value: 'Opay' },
	{ id: 13, label: 'PalmPay', value: 'PalmPay' },
	{ id: 14, label: 'Polaris Bank', value: 'Polaris Bank' },
	{ id: 15, label: 'Providus Bank', value: 'Providus Bank' },
	{ id: 16, label: 'Stanbic IBTC Bank', value: 'Stanbic IBTC Bank' },
	{ id: 17, label: 'Standard Chartered Bank', value: 'Standard Chartered Bank' },
	{ id: 18, label: 'Sterling Bank', value: 'Sterling Bank' },
	{ id: 19, label: 'SunTrust Bank', value: 'SunTrust Bank' },
	{ id: 20, label: 'Titan Trust Bank', value: 'Titan Trust Bank' },
	{ id: 21, label: 'Union Bank of Nigeria', value: 'Union Bank of Nigeria' },
	{ id: 22, label: 'United Bank for Africa (UBA)', value: 'United Bank for Africa' },
	{ id: 23, label: 'Unity Bank', value: 'Unity Bank' },
	{ id: 24, label: 'Wema Bank', value: 'Wema Bank' },
	{ id: 25, label: 'Zenith Bank', value: 'Zenith Bank' },
];

export interface IVolunteerRoleDescription {
	title: string;
	summary: string;
	responsibilities: string[];
	requirements: string[];
}

export interface IVolunteerRoleOption {
	id: number;
	label: string;
	value: string;
	description: IVolunteerRoleDescription;
}

export const VOLUNTEER_ROLE_OPTIONS: IVolunteerRoleOption[] = [
	{
		id: 1,
		label: 'Polling Unit Collation Agent',
		value: 'EC8A',
		description: {
			title: 'Polling Unit Collation Agent (EC8A)',
			summary:
				'Stationed at a specific polling unit to monitor and record election results as announced by the presiding officer.',
			responsibilities: [
				'Monitor the vote counting process at your assigned polling unit',
				'Record results accurately on the EC8A form as announced',
				'Take clear photos of the official result sheet',
				'Submit results promptly via the mobile app',
				'Report any irregularities or discrepancies immediately',
			],
			requirements: [
				'Must be present from opening to close of the polling unit',
				'Familiar with the local area and polling unit location',
				'Objective and non-partisan throughout the process',
				'Proficient with smartphone and mobile apps',
			],
		},
	},
	{
		id: 2,
		label: 'Ward Collation Officer',
		value: 'EC8B',
		description: {
			title: 'Ward Collation Officer (EC8B)',
			summary:
				'Assigned to a ward collation center to observe the aggregation of results from multiple polling units within the ward.',
			responsibilities: [
				'Observe the collation process at the ward level',
				'Verify that EC8A results from polling units match announced figures',
				'Record aggregated results on the EC8B form',
				'Document any discrepancies between submitted and collated results',
				'Coordinate with polling unit agents for verification',
			],
			requirements: [
				'Thorough understanding of the collation process',
				'Strong attention to detail and numerical accuracy',
				'Previous polling unit monitoring experience preferred',
				'Ability to work under pressure during collation',
			],
		},
	},
	{
		id: 3,
		label: 'Local Government Collation Officer',
		value: 'EC8C',
		description: {
			title: 'Local Government Collation Officer (EC8C)',
			summary:
				'Observe the collation of ward results at the Local Government Area (LGA) level to ensure accuracy and transparency.',
			responsibilities: [
				'Monitor the aggregation of ward results at LGA headquarters',
				'Verify that EC8B ward results match the announced figures',
				'Record LGA-level results on the EC8C form',
				'Coordinate with ward collation officers for cross-verification',
				'Report significant discrepancies to the central team',
			],
			requirements: [
				'Comprehensive understanding of the electoral collation process',
				'Previous election monitoring experience strongly preferred',
				'Strong organizational and coordination skills',
				'Ability to manage multiple data streams simultaneously',
			],
		},
	},
];
